import React, { Component, ErrorInfo, ReactNode } from 'react';

import logo from 'assets/images/logo.svg';
import 'app.css';

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className='min-h-screen bg-gradient-to-b from-[#f9f9f9] to-[#e8f5e9] flex flex-col'
          style={{ fontFamily: 'Larsseit-Regular, system-ui, sans-serif' }}
        >
          <header className='p-6'>
            <img src={logo} alt='Intellitix' className='h-8' />
          </header>

          <div className='flex-1 flex items-center justify-center px-6'>
            <div className='max-w-md w-full text-center'>
              <div className='w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 rounded-full bg-white shadow-sm border border-[#E5E7EB] flex items-center justify-center'>
                <svg
                  width='28'
                  height='28'
                  viewBox='0 0 24 24'
                  fill='none'
                  className='sm:w-8 sm:h-8'
                >
                  <circle cx='12' cy='12' r='9' stroke='#F97316' strokeWidth='2' />
                  <path
                    d='M12 8v4m0 4h.01'
                    stroke='#F97316'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>

              <h1
                className='text-[24px] sm:text-[28px] font-bold mb-4'
                style={{
                  fontFamily: 'Larsseit-Bold, system-ui, sans-serif',
                  color: '#00303B',
                  fontWeight: 700,
                }}
              >
                Oops, something broke
              </h1>

              <p className='text-[15px] sm:text-[16px] text-[#517077] mb-6 leading-relaxed'>
                This page ran into an unexpected error.
                <br />
                Our team has been notified and is looking into it.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className='mb-8 group'>
                  <summary className='text-[14px] text-[#6B7280] cursor-pointer hover:text-[#374151] list-none flex items-center justify-center gap-1 transition-colors'>
                    <span>Technical details</span>
                    <svg
                      className='w-4 h-4 transition-transform group-open:rotate-180'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M19 9l-7 7-7-7'
                      />
                    </svg>
                  </summary>
                  <pre className='mt-3 px-4 py-3 bg-white shadow-sm border border-[#E5E7EB] rounded-xl text-[12px] sm:text-[13px] text-[#6B7280] font-mono overflow-auto max-h-32 text-left'>
                    {this.state.error.message}
                  </pre>
                </details>
              )}

              <button
                onClick={this.handleGoHome}
                className='px-10 py-3 bg-[#4ADE80] text-[#00303B] text-[15px] rounded-xl shadow-sm hover:bg-[#38D989] active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-[#38D989] focus:ring-offset-2'
                style={{ fontFamily: 'Larsseit-Medium, system-ui, sans-serif' }}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
